import {
  useContext,
  createContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  ReactNode,
} from 'react'
import { AuthService } from '../lib/services/auth'
import { Role, User } from '../types/index'
import { instance } from '../lib/http/axios'
import { LoadingSpinner } from '../components/layout/loading-spinner'
import { useNavigate } from 'react-router-dom'

export interface LoginParams {
  email: string
  password: string
  redirectUrl?: string
}

export interface SignupParams {
  email: string
  password: string
  firstName: string
  lastName: string
  phone: string
  address: string
  city: string
  state: string
  zip: string
  country: string
  companyName: string
  siret: string
  tva: string
}

interface AuthContextProps {
  authenticated: boolean
  user: User | null
  setUser: Dispatch<SetStateAction<User | null>>
  logout: () => void
  login: (params: LoginParams) => Promise<void>
  signup: (params: SignupParams) => Promise<void>
  isLoading: boolean
  loginSuccessCallback: (token: string) => void
  checkUserRequiredInformation: () => boolean
  isSuper: boolean
}

export const AuthContext = createContext<AuthContextProps>(
  {} as AuthContextProps
)

export const REDIRECT_PAGE = 'REDIRECT_PAGE'
export const TOKEN = 'frada_token'

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const navigate = useNavigate()

  const initlizeUser = async (token: string) => {
    instance.defaults.headers.common['Authorization'] = `Bearer ${token}`

    try {
      const response = ((await AuthService.me()) as any).data

      setUser(response as any)

      localStorage.setItem('role', response?.role)

      return response
    } catch (e) {
      localStorage.removeItem(TOKEN)
      localStorage.removeItem('role')

      console.error(e)
      window.location.reload()
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const token = localStorage.getItem(TOKEN)
    if (token) {
      initlizeUser(token)
    } else {
      setIsLoading(false)
    }
  }, [])

  const loginSuccessCallback = async (token: string) => {
    const authUser = await initlizeUser(token)
    localStorage.setItem(TOKEN, token)

    if (authUser?.role === Role.User) {
      navigate('/bo/new-delivery')
    } else {
      navigate('/bo/dashboard')
    }
    localStorage.removeItem(REDIRECT_PAGE)
  }

  const login = async ({ email, password }: LoginParams) => {
    const response = await AuthService.login({ email, password })
    await loginSuccessCallback(response.token)
  }

  const signup = async (params: SignupParams) => {
    const response = await AuthService.signUp(params)
    await loginSuccessCallback(response.token)
  }

  const logout = () => {
    localStorage.removeItem(TOKEN)
    localStorage.removeItem('role')
    setUser(null)
    AuthService.logout()

    localStorage.removeItem(REDIRECT_PAGE)
    navigate('/')
  }

  const checkUserRequiredInformation = (): boolean => {
    if (!user) {
      return false
    }

    if (
      user.address === null ||
      user.phone === null ||
      user.companyName === null ||
      user.siren === null ||
      user.tva === null ||
      user.zipCode === null ||
      user.city === null ||
      user.country === null ||
      user.firstName === null ||
      user.lastName === null ||
      user.iban === null ||
      user.swift === null
    ) {
      return false
    }

    return true
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <AuthContext.Provider
      value={{
        authenticated: !isLoading && !!user,
        user,
        setUser,
        logout,
        login,
        signup,
        isLoading,
        loginSuccessCallback,
        checkUserRequiredInformation,
        isSuper: user?.email === 'frada.express@gmail.com',
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => useContext(AuthContext)

export default useAuth
